import { useRef } from 'react'

import {
  AsyncSelect,
  type AsyncSelectLoadOptions,
  type IAsyncSelectProps,
  type SelectValue,
} from '@procurify/ui'

import { useFormatDate } from '@webapp/core/components/FormattedDate/useFormatDate'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { financialAccountRepository } from '../../data-access/repositories/FinancialAccountRepository'
import { parseStatementPeriod } from '../../utils/parseStatementPeriod'

const STATEMENT_PERIODS_PAGE_SIZE = 20

type StatementPeriodOption = SelectValue<string>

export interface IStatementPeriodSelectProps extends Omit<
  IAsyncSelectProps<StatementPeriodOption>,
  'value' | 'onChange' | 'loadOptions'
> {
  financialAccountId: number
  /** Statement period in `"YYYY-MM"` format. */
  value: Nullable<string>
  onChange: (period: string) => void
}

export const StatementPeriodSelect = ({
  financialAccountId,
  value,
  onChange,
  ...props
}: IStatementPeriodSelectProps) => {
  const intl = useProcurifyIntl()
  const formatDate = useFormatDate()
  const hasAutoSelectedRef = useRef(false)

  const toOption = (period: string): StatementPeriodOption => ({
    label: formatDate(parseStatementPeriod(period), {
      month: 'long',
      year: 'numeric',
    }),
    value: period,
  })

  const loadOptions: AsyncSelectLoadOptions<StatementPeriodOption> = async (
    _inputValue,
    pagination,
    callback
  ) => {
    try {
      const res = await financialAccountRepository.getStatementPeriods({
        financialAccountId,
        page: pagination.page,
        pageSize: STATEMENT_PERIODS_PAGE_SIZE,
      })

      const options = res.data.map(toOption)

      if (
        !hasAutoSelectedRef.current &&
        !value &&
        pagination.page === 1 &&
        options[0]
      ) {
        hasAutoSelectedRef.current = true
        onChange(options[0].value)
      }

      callback({
        options,
        pagination: {
          hasMore: res.pagination
            ? res.pagination.page < res.pagination.numPages
            : false,
          page: pagination.page + 1,
        },
      })
    } catch {
      callback({ options: [], pagination: { hasMore: false, page: 1 } })
    }
  }

  return (
    <AsyncSelect<StatementPeriodOption>
      inputValue=''
      defaultOptions
      cacheOptions
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.selectFieldPlaceholder)}
      value={value ? toOption(value) : null}
      onChange={(option: StatementPeriodOption) => onChange(option.value)}
      isSearchable={false}
      noOptionsMessage={() => intl.formatMessage(messages.noOptionsMessage)}
      {...props}
    />
  )
}

StatementPeriodSelect.displayName = 'StatementPeriodSelect'
